import { Component } from 'react'
import JwtDecode from 'jwt-decode'
import PropTypes from 'prop-types'
import axios from 'axios'

class TokenManager extends Component {
    constructor(props) {
        super(props)

        this.expirationBuffer = 30
    }

    /**
     * Initialize launch progress
     */
    componentDidMount() {
        this.props.setClientLaunchProgress(1)
        this.validateToken()
    }

    /**
     * Validating whether we have a genuine JWT
     * and whether the token has expired
     */
    validateToken = () => {
        try {
            this.props.setClientLaunchProgress(10)

            // Decode the JWT
            const jwt = JwtDecode(this.props.accessToken)

            // Get the current unix timestamp in seconds
            const currentTime = Math.round(new Date().getTime() / 1000)

            if (
                jwt.exp - this.expirationBuffer > currentTime &&
                (jwt.scp && jwt.scp.indexOf('eveClientLogin') !== -1) === true
            ) {
                // If the access token is still valid we set progress 100% and launch a client
                this.props.launchEve(this.props.accessToken)
                this.props.setClientLaunchProgress(100)

                // Unmount the component
                this.props.unMount()
                return
            } else if (!jwt.scp || jwt.scp.indexOf('eveClientLogin') === -1) {
                console.info('Scope is missing from token, attempting a refresh')
            }
            // Token has expired, so we refresh it before launching a client
            this.refreshToken(this.props.accessToken)
            return
        } catch (e) {
            // Expect to hit this during transition period, while some users have old access token
            if (e.message.indexOf('Invalid token specified' !== -1)) {
                // Log what we're doing
                console.info(
                    `Obsolete token detected for ${this.props.userId}, refreshing for a v2 token`,
                )

                // Replace current tokens with new JWT's
                this.refreshToken('refreshing.withfake.jwt')
            } else {
                console.info(`Error while decoding token: ${e.message}`)
            }
        }
    }

    /**
     * Updates the tokens in state and launches client using the new access token
     * @param {object} tokens
     */
    updateTokensAndLaunchClient = (tokens) => {
        // Update client launch progress
        this.props.setClientLaunchProgress(70)

        // Update the user's tokens in state
        this.props.setTokens(tokens.AccessToken, tokens.RefreshToken)

        // Since we have just received a new set of tokens
        // we immediately use the access token to launch a client
        this.props.launchEve(tokens.AccessToken)
        this.props.setClientLaunchProgress(100)

        // Unmount the component
        this.props.unMount()
    }

    /**
     * Use the refresh token to acquire a new pair of tokens,
     * update both in state
     * @param {string} accessToken
     */
    refreshToken = (accessToken) => {
        // Validate that a refresh token has been set
        if (this.props.refreshTokenService == null || this.props.refreshTokenService === '') {
            console.info('Configuration error: Refresh token service is missing')

            // Reset launch progress
            this.props.setClientLaunchProgress(0)

            // Display refresh error message
            this.props.displayRefreshingError()

            // Unmount the component
            this.props.unMount()
            return null
        }

        // Create a log entry about refreshing beginning
        console.info(`Refreshing access token for ${this.props.userId}`)

        // Update client progress
        this.props.setClientLaunchProgress(60)

        // Sent the refresh token to the launcher token service
        // and retrieve a new access token
        axios({
            method: 'post',
            url: this.props.refreshTokenService,
            data: {
                token: this.props.refreshToken,
                accessToken,
            },
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then(response => this.updateTokensAndLaunchClient(response.data))
            .catch((error) => {
                // This message is used through all the errors
                const commonMessage = `Encountered an error while refreshing token for ${
                    this.props.userId
                }:`

                // Reset the client launch progressbar
                this.props.setClientLaunchProgress(0)

                if (error.response == null && error.message === 'Network Error') {
                    // No connection could be established to the token service
                    // Log the attempt
                    console.info(
                        `${commonMessage} Token refresh request failed, could not connect to ${
                            this.props.refreshTokenService
                        }`,
                    )

                    // Display generic error message to the user
                    // TODO: Change this to a network related error message
                    this.props.displayRefreshingError()
                } else if (error.response != null) {
                    // Retrieve status code & text from response
                    const statusText = error.response.statusText
                    const statusCode = error.response.status

                    // Create a log entry with the code & text
                    console.info(
                        `${commonMessage} Token service responded with ${statusCode} ${statusText}`,
                    )

                    // Dictate which type of error message we should display
                    if (error.response.status === 400) {
                        // The SSO responded with Bad Request
                        // This means that the token is likely invalid,
                        // and we should ask the user to relogin
                        this.props.displayAutoRefreshFail()
                    } else {
                        // Display error message on UI
                        this.props.displayRefreshingError()
                    }
                } else {
                    // Response is null, but not a network error
                    // Log the error message itself
                    console.info(`${commonMessage} ${error.message}`)

                    // An unaccounted for error occurred
                    // Display a generic error message about refresh failing
                    this.props.displayRefreshingError()
                }

                // Unmount the component
                this.props.unMount()
            })
    }

    render() {
        return null
    }
}

TokenManager.propTypes = {
    userId: PropTypes.string,
    unMount: PropTypes.func,
    launchEve: PropTypes.func, // Start game client
    setTokens: PropTypes.func, // Update tokens on refresh
    accessToken: PropTypes.string, // Current token
    refreshToken: PropTypes.string, // Current token
    refreshTokenService: PropTypes.string,
    // clientLaunchProgress: PropTypes.number,
    setClientLaunchProgress: PropTypes.func, // Launch progress
    displayAutoRefreshFail: PropTypes.func,
    displayRefreshingError: PropTypes.func,
}

export default TokenManager



// WEBPACK FOOTER //
// ./src/features/Users/components/UserList/components/TokenManager/component.js